import React, { useState, useRef, useCallback } from 'react';
import { Bug, X, Mic, MicOff, Camera, Send, Loader2, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import html2canvas from 'html2canvas';

import { getBackendUrl } from '../services/origin';
import { apiUpload } from '../services/publicApi';
const API = getBackendUrl();

const PRIORITIES = [
  { value: 'critico', label: 'Critico', icon: AlertTriangle, color: '#EF4444', bg: '#FEE2E2' },
  { value: 'importante', label: 'Importante', icon: AlertCircle, color: '#F59E0B', bg: '#FEF3C7' },
  { value: 'minore', label: 'Minore', icon: Info, color: '#3B82F6', bg: '#DBEAFE' },
];

export default function BugReportButton({ userRole = '', userName = '', brandName = '' }) {
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState('');
  const [priority, setPriority] = useState('importante');
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [screenshotBlob, setScreenshotBlob] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);

  const takeScreenshot = useCallback(async () => {
    try {
      const canvas = await html2canvas(document.body, {
        scale: 0.5,
        logging: false,
        useCORS: true,
        ignoreElements: (el) => el.dataset?.testid === 'bug-report-modal',
      });
      canvas.toBlob((blob) => {
        setScreenshotBlob(blob);
        setScreenshotPreview(URL.createObjectURL(blob));
      }, 'image/jpeg', 0.6);
    } catch {
      // Fallback: no screenshot
    }
  }, []);

  const openModal = useCallback(() => {
    setOpen(true);
    setSent(false);
    setDesc('');
    setPriority('importante');
    setAudioBlob(null);
    setScreenshotBlob(null);
    setScreenshotPreview(null);
    // Auto-screenshot on open
    setTimeout(() => takeScreenshot(), 300);
  }, [takeScreenshot]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunks.current = [];
      recorder.ondataavailable = (e) => audioChunks.current.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(audioChunks.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      mediaRecorder.current = recorder;
      setRecording(true);
    } catch {
      alert('Microfono non disponibile');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && recording) {
      mediaRecorder.current.stop();
      setRecording(false);
    }
  };

  const submit = async () => {
    if (!desc.trim() && !audioBlob) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('description', desc);
      formData.append('priority', priority);
      formData.append('page_url', window.location.href);
      formData.append('user_role', userRole);
      formData.append('user_name', userName);
      formData.append('brand_name', brandName);
      if (screenshotBlob) formData.append('screenshot', screenshotBlob, 'screenshot.jpg');
      if (audioBlob) formData.append('audio', audioBlob, 'audio.webm');

      await apiUpload('/api/bug-reports', formData);
      setSent(true);
    } catch {
      alert('Errore invio segnalazione');
    }
    setSending(false);
  };

  return (
    <>
      {/* Floating button */}
      <button onClick={openModal} data-testid="bug-report-btn"
        className="fixed bottom-24 right-6 z-[9999] w-11 h-11 rounded-full bg-[#1A1A1A] text-white shadow-xl flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
        title="Segnala un bug">
        <Bug size={18} />
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-[10000] flex items-end md:items-center justify-center" data-testid="bug-report-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => !sending && setOpen(false)} />
          <div className="relative w-full max-w-md bg-white rounded-t-2xl md:rounded-2xl shadow-2xl p-5 space-y-4 max-h-[85vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bug size={18} className="text-[#D44E20]" />
                <h3 className="font-bold text-[#1A1A1A]">Segnala un problema</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>

            {sent ? (
              <div className="text-center py-6">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <Send size={24} className="text-green-600" />
                </div>
                <p className="font-bold text-[#1A1A1A]">Segnalazione inviata!</p>
                <p className="text-xs text-slate-500 mt-1">Grazie per il tuo aiuto. Il team Lazo la esaminera al piu presto.</p>
                <button onClick={() => setOpen(false)} className="mt-4 px-6 py-2 bg-[#1A1A1A] text-white text-sm font-semibold rounded-xl">Chiudi</button>
              </div>
            ) : (
              <>
                {/* Priority */}
                <div>
                  <label className="text-[10px] text-slate-500 font-semibold block mb-1.5">Priorita</label>
                  <div className="flex gap-2">
                    {PRIORITIES.map(p => (
                      <button key={p.value} onClick={() => setPriority(p.value)} data-testid={`priority-${p.value}`}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border transition-all ${
                          priority === p.value ? 'shadow-sm' : 'opacity-50'
                        }`}
                        style={{
                          backgroundColor: priority === p.value ? p.bg : '#f8fafc',
                          borderColor: priority === p.value ? p.color + '40' : '#e2e8f0',
                          color: priority === p.value ? p.color : '#94A3B8',
                        }}>
                        <p.icon size={13} /> {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-[10px] text-slate-500 font-semibold block mb-1">Descrizione</label>
                  <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm resize-none focus:border-[#D44E20] focus:ring-2 focus:ring-[#D44E20]/20 outline-none"
                    data-testid="bug-desc-input"
                    placeholder="Descrivi il problema che hai riscontrato..." />
                </div>

                {/* Audio recorder */}
                <div className="flex items-center gap-3">
                  {recording ? (
                    <button onClick={stopRecording} data-testid="stop-recording-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white text-xs font-semibold rounded-xl animate-pulse">
                      <MicOff size={14} /> Ferma registrazione
                    </button>
                  ) : (
                    <button onClick={startRecording} data-testid="start-recording-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 text-xs font-semibold rounded-xl hover:bg-slate-200 transition-colors">
                      <Mic size={14} /> {audioBlob ? 'Registra di nuovo' : 'Registra audio'}
                    </button>
                  )}
                  {audioBlob && !recording && (
                    <span className="text-[10px] text-green-600 font-semibold">Audio registrato</span>
                  )}
                </div>

                {/* Screenshot preview */}
                {screenshotPreview && (
                  <div>
                    <label className="text-[10px] text-slate-500 font-semibold block mb-1">Screenshot catturato</label>
                    <div className="relative rounded-xl overflow-hidden border border-slate-200">
                      <img src={screenshotPreview} alt="Screenshot" className="w-full h-32 object-cover object-top" />
                      <button onClick={takeScreenshot} className="absolute top-2 right-2 px-2 py-1 bg-white/90 text-[10px] font-semibold rounded-lg text-slate-600 hover:bg-white flex items-center gap-1">
                        <Camera size={10} /> Riscatta
                      </button>
                    </div>
                  </div>
                )}
                {!screenshotPreview && (
                  <button onClick={takeScreenshot} className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 text-xs font-semibold rounded-xl hover:bg-slate-200 transition-colors">
                    <Camera size={14} /> Cattura screenshot
                  </button>
                )}

                {/* Submit */}
                <button onClick={submit} disabled={sending || (!desc.trim() && !audioBlob)} data-testid="submit-bug-btn"
                  className="w-full py-3 bg-[#D44E20] text-white font-bold text-sm rounded-xl flex items-center justify-center gap-2 hover:bg-[#B8411A] active:scale-[0.98] transition-all disabled:opacity-40">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {sending ? 'Invio...' : 'Invia segnalazione'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
